// Gating for the "show translation" affordance under answer bodies: decide
// whether an answer's text is (probably) not in the viewer's language.
//
// Precedence:
//   1. A reliable answers.detected_lang code from the server.
//   2. For undetermined or legacy rows, a cheap Unicode-script heuristic: only
//      fires when the dominant script differs from the viewer language's
//      script, so Latin↔Latin pairs (e.g. a Spanish post for an English viewer)
//      stay hidden until the server stamps a real code.

declare const gon: {
    currentUser?: {
        options?: {
            preferredLanguage?: string
        }
    }
    [key: string]: unknown
}

const UNDETERMINED = 'und'

// Sampling caps so stream renders never scan unbounded text.
const HEURISTIC_SAMPLE_CHARS = 400
const HEURISTIC_MIN_LETTERS = 12
const HEURISTIC_DOMINANCE = 0.6

const URL_PATTERN = /(?:https?:\/\/|www\.)\S+/gi
const HANDLE_PATTERN = /[@#][\w.-]+/g
const MEME_PATTERN = /\[\[\s*[\w\s]+\s*\]\]/g

type ScriptBucket =
    | 'latin'
    | 'cyrillic'
    | 'han'
    | 'kana'
    | 'hangul'
    | 'arabic'
    | 'hebrew'
    | 'greek'
    | 'thai'
    | 'devanagari'

const DENSE_SCRIPT_BUCKETS = new Set<ScriptBucket>(['han', 'kana', 'hangul'])

const SCRIPT_BUCKETS: Array<{ bucket: ScriptBucket; pattern: RegExp }> = [
    { bucket: 'kana', pattern: /[\p{Script=Hiragana}\p{Script=Katakana}]/u },
    { bucket: 'han', pattern: /\p{Script=Han}/u },
    { bucket: 'hangul', pattern: /\p{Script=Hangul}/u },
    { bucket: 'cyrillic', pattern: /\p{Script=Cyrillic}/u },
    { bucket: 'arabic', pattern: /\p{Script=Arabic}/u },
    { bucket: 'hebrew', pattern: /\p{Script=Hebrew}/u },
    { bucket: 'greek', pattern: /\p{Script=Greek}/u },
    { bucket: 'thai', pattern: /\p{Script=Thai}/u },
    { bucket: 'devanagari', pattern: /\p{Script=Devanagari}/u },
    { bucket: 'latin', pattern: /\p{Script=Latin}/u },
]

// Scripts a viewer of a given language reads natively. Any language not listed
// is assumed Latin-script. Han is expected for Japanese/Korean viewers so
// kanji/hanja-heavy text doesn't false-positive.
const LANG_EXPECTED_BUCKETS: Record<string, ScriptBucket[]> = {
    ru: ['cyrillic'],
    uk: ['cyrillic'],
    be: ['cyrillic'],
    bg: ['cyrillic'],
    mk: ['cyrillic'],
    sr: ['cyrillic', 'latin'],
    zh: ['han'],
    ja: ['kana', 'han'],
    ko: ['hangul', 'han'],
    ar: ['arabic'],
    fa: ['arabic'],
    ur: ['arabic'],
    he: ['hebrew'],
    yi: ['hebrew'],
    el: ['greek'],
    th: ['thai'],
    hi: ['devanagari'],
    mr: ['devanagari'],
    ne: ['devanagari'],
}

export function primaryLanguageSubtag(raw: unknown): string | null {
    if (typeof raw !== 'string') return null
    const primary = raw.trim().toLowerCase().split('-')[0]
    return /^[a-z]{2,3}$/.test(primary) ? primary : null
}

export function getViewerLanguage(): string {
    try {
        return primaryLanguageSubtag(gon?.currentUser?.options?.preferredLanguage) || 'en'
    } catch {
        return 'en'
    }
}

function stripNoise(text: string): string {
    return text.replace(URL_PATTERN, ' ').replace(HANDLE_PATTERN, ' ')
}

function isMemeOnlyContent(text: string): boolean {
    // Meme labels are navigation tokens, not prose. A post containing only
    // `[[ meme ]]` tokens should not offer to translate those labels.
    const trimmed = text.trim()
    return trimmed.startsWith('[[') && !trimmed.replace(MEME_PATTERN, '').trim()
}

function classifyLetter(char: string): ScriptBucket | null {
    for (const { bucket, pattern } of SCRIPT_BUCKETS) {
        if (pattern.test(char)) return bucket
    }
    return null
}

function dominantScriptLooksForeign(text: string, viewerLang: string): boolean {
    const sample = stripNoise(text).slice(0, HEURISTIC_SAMPLE_CHARS)

    const counts: Partial<Record<ScriptBucket, number>> = {}
    let totalLetters = 0
    for (const char of sample) {
        const bucket = classifyLetter(char)
        if (!bucket) continue
        counts[bucket] = (counts[bucket] || 0) + 1
        totalLetters += 1
    }
    if (!totalLetters) return false

    let dominant: ScriptBucket | null = null
    let dominantCount = 0
    for (const [bucket, count] of Object.entries(counts) as Array<[ScriptBucket, number]>) {
        if (count > dominantCount) {
            dominant = bucket
            dominantCount = count
        }
    }
    if (!dominant) return false
    // Han, kana, and hangul identify their writing system without enough prose
    // to identify the exact language reliably.
    if (!DENSE_SCRIPT_BUCKETS.has(dominant) && totalLetters < HEURISTIC_MIN_LETTERS) return false
    if (dominantCount < totalLetters * HEURISTIC_DOMINANCE) return false

    const expected = LANG_EXPECTED_BUCKETS[viewerLang] || ['latin']
    return !expected.includes(dominant)
}

type TranslatableAnswer = {
    content?: string | null
    detected_lang?: string | null
}

export function shouldOfferTranslation(
    answer: TranslatableAnswer | null | undefined,
    viewerLang: string = getViewerLanguage(),
): boolean {
    const content = typeof answer?.content === 'string' ? answer.content : ''
    if (!content.trim() || isMemeOnlyContent(content)) return false

    const rawDetected = answer?.detected_lang
    const detected = rawDetected === UNDETERMINED ? null : primaryLanguageSubtag(rawDetected)
    if (detected) return detected !== (primaryLanguageSubtag(viewerLang) || 'en')

    return dominantScriptLooksForeign(content, viewerLang)
}
